import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { Header, BottomNav, Sidebar } from '@/components/layout'
import { PageErrorBoundary, Skeleton } from '@/components/ui'
import { Home } from '@/pages' // Keep Home eager-loaded for fast initial render
import { Onboarding } from '@/pages/Onboarding'
import { DailyRewardsModal } from '@/components/DailyRewardsModal'
import { OfflineIndicator } from '@/components/OfflineIndicator'
import { useOnboardingStore } from '@/stores/useOnboardingStore'
import { useDailyRewardsStore } from '@/stores/useDailyRewardsStore'
import { useTaskStore } from '@/stores/useTaskStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { initLogger, identifyUser, trackEvent, logError, LogEvents } from '@/lib/logger'
import { cleanupLegacyData } from '@/lib/sync'
import { initOneSignal, setExternalUserId, removeExternalUserId, isOneSignalConfigured } from '@/lib/onesignal'

// Lazy load pages for better initial bundle size
const Quests = lazy(() => import('@/pages/Quests').then(m => ({ default: m.Quests })))
const Pomodoro = lazy(() => import('@/pages/Pomodoro').then(m => ({ default: m.Pomodoro })))
const Habits = lazy(() => import('@/pages/Habits').then(m => ({ default: m.Habits })))
const Routines = lazy(() => import('@/pages/Routines').then(m => ({ default: m.Routines })))
const Projects = lazy(() => import('@/pages/Projects').then(m => ({ default: m.Projects })))
const Gacha = lazy(() => import('@/pages/Gacha').then(m => ({ default: m.Gacha })))
const Closet = lazy(() => import('@/pages/Closet').then(m => ({ default: m.Closet })))
const Stats = lazy(() => import('@/pages/Stats').then(m => ({ default: m.Stats })))
const Achievements = lazy(() => import('@/pages/Achievements').then(m => ({ default: m.Achievements })))
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })))
const Auth = lazy(() => import('@/pages/Auth').then(m => ({ default: m.Auth })))
const Partners = lazy(() => import('@/pages/Partners'))

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="flex-1 p-4">
      <Skeleton className="h-8 w-48 mb-4" />
      <Skeleton className="h-32 w-full mb-3" />
      <Skeleton className="h-32 w-full mb-3" />
      <Skeleton className="h-32 w-full" />
    </div>
  )
}

// Initialize logger on app load
initLogger()

// Clean up legacy data with invalid IDs
cleanupLegacyData()

export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showDailyRewards, setShowDailyRewards] = useState(false)
  const { hasCompletedOnboarding } = useOnboardingStore()
  const { canClaimToday } = useDailyRewardsStore()
  const { checkAndResetRecurring } = useTaskStore()
  const { level, activeCharacterId } = useUserStore()
  const { contentLevel } = useSettingsStore()
  const { initialize: initAuth, user } = useAuthStore()

  // Initialize auth on app load
  useEffect(() => {
    initAuth()
  }, [initAuth])

  // Initialize OneSignal on app load
  useEffect(() => {
    if (isOneSignalConfigured()) {
      initOneSignal()
    }
  }, [])

  // Link OneSignal to Supabase user when authenticated
  useEffect(() => {
    if (isOneSignalConfigured() && hasCompletedOnboarding) {
      if (user?.id) {
        setExternalUserId(user.id)
      } else {
        removeExternalUserId()
      }
    }
  }, [user?.id, hasCompletedOnboarding])

  // Initialize user identification for LogRocket
  useEffect(() => {
    if (hasCompletedOnboarding) {
      const username = useUserStore.getState().username
      // Use Supabase user ID if available, otherwise generate a local ID
      const userId = user?.id || `local_${Date.now()}`
      identifyUser(userId, {
        name: username || 'Anonymous',
        email: user?.email,
        level,
        activeCharacter: activeCharacterId,
        contentLevel,
      })
      trackEvent(LogEvents.APP_OPENED, { level, activeCharacter: activeCharacterId })
    }
  }, [hasCompletedOnboarding, level, activeCharacterId, contentLevel, user])

  // Check and reset recurring tasks on app load
  useEffect(() => {
    if (hasCompletedOnboarding) {
      checkAndResetRecurring()
    }
  }, [hasCompletedOnboarding, checkAndResetRecurring])

  // Handle global errors for logging
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    logError(error, { componentStack: errorInfo.componentStack || 'unknown' })
  }

  // Show daily rewards modal if user can claim
  useEffect(() => {
    if (hasCompletedOnboarding && canClaimToday()) {
      // Small delay to let the app render first
      const timer = setTimeout(() => {
        setShowDailyRewards(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [hasCompletedOnboarding, canClaimToday])

  // Show onboarding for new users
  if (!hasCompletedOnboarding) {
    return (
      <PageErrorBoundary onError={handleError}>
        <Onboarding />
      </PageErrorBoundary>
    )
  }

  return (
    <PageErrorBoundary onError={handleError}>
      <OfflineIndicator />
      <div className="min-h-screen min-h-dvh flex flex-col bg-bg-dark">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/quests" element={<Quests />} />
              <Route path="/pomodoro" element={<Pomodoro />} />
              <Route path="/habits" element={<Habits />} />
              <Route path="/routines" element={<Routines />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/gacha" element={<Gacha />} />
              <Route path="/closet" element={<Closet />} />
              <Route path="/stats" element={<Stats />} />
              <Route path="/achievements" element={<Achievements />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/partners" element={<Partners />} />
            </Routes>
          </AnimatePresence>
        </Suspense>
        <BottomNav />

        {/* Daily Rewards Modal */}
        <DailyRewardsModal
          isOpen={showDailyRewards}
          onClose={() => setShowDailyRewards(false)}
        />
      </div>
    </PageErrorBoundary>
  )
}
